import { useState, useRef, useCallback } from 'preact/hooks';
import { authFetch } from '../api/authFetch';

interface Props {
  open: boolean;
  onClose: () => void;
  /** Default project to upload to */
  defaultProject?: string;
  /** All available project names for the picker */
  projectNames?: string[];
  /** Called after a successful upload with the new markdownPath */
  onUploaded?: (markdownPath: string, title: string) => void;
}

type Stage = 'idle' | 'uploading' | 'done' | 'error';

export function UploadModal({ open, onClose, defaultProject, projectNames = [], onUploaded }: Props) {
  const [stage, setStage] = useState<Stage>('idle');
  const [projectKey, setProjectKey] = useState(defaultProject || '');
  const [dragOver, setDragOver] = useState(false);
  const [result, setResult] = useState<{ markdownPath: string; title: string; wordCount: number } | null>(null);
  const [errorMsg, setErrorMsg] = useState('');
  const [progress, setProgress] = useState<string>('');
  const fileRef = useRef<File | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const reset = () => {
    setStage('idle');
    setResult(null);
    setErrorMsg('');
    setProgress('');
    fileRef.current = null;
    if (inputRef.current) inputRef.current.value = '';
  };

  const handleClose = () => { reset(); onClose(); };

  const doUpload = useCallback(async (file: File) => {
    if (!projectKey.trim()) {
      setErrorMsg('Please select a project first');
      return;
    }
    fileRef.current = file;
    setStage('uploading');
    setProgress('Uploading…');

    // Simulate stage progress (backend is synchronous — no SSE needed)
    const progressTimer = setTimeout(() => setProgress('Extracting text…'), 1500);
    const progressTimer2 = setTimeout(() => setProgress('Structuring markdown…'), 3500);

    try {
      const fd = new FormData();
      fd.append('projectKey', projectKey.trim());
      fd.append('file', file, file.name);

      const res = await authFetch('/api/files/upload', { method: 'POST', body: fd });
      clearTimeout(progressTimer);
      clearTimeout(progressTimer2);

      if (!res.ok) {
        const text = await res.text().catch(() => `HTTP ${res.status}`);
        throw new Error(text || `Upload failed (${res.status})`);
      }
      const data = await res.json();
      setResult({ markdownPath: data.markdownPath, title: data.title, wordCount: data.wordCount });
      setStage('done');
      onUploaded?.(data.markdownPath, data.title);
    } catch (err: any) {
      clearTimeout(progressTimer);
      clearTimeout(progressTimer2);
      setErrorMsg(err.message || 'Upload failed');
      setStage('error');
    }
  }, [projectKey, onUploaded]);

  const handleFileSelect = (e: Event) => {
    const input = e.target as HTMLInputElement;
    const file = input.files?.[0];
    if (file) doUpload(file);
  };

  const handleDrop = (e: DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer?.files[0];
    if (file) doUpload(file);
  };

  const openFile = (path: string) => {
    window.dispatchEvent(new CustomEvent('open-file-viewer', { detail: { path } }));
    handleClose();
  };

  if (!open) return null;

  return (
    <div class="upload-modal-overlay" onClick={handleClose}>
      <div class="upload-modal" onClick={(e) => e.stopPropagation()}>
        <div class="upload-modal-header">
          <span class="upload-modal-title">Upload PDF</span>
          <button class="upload-modal-close" onClick={handleClose} aria-label="Close">✕</button>
        </div>

        {/* Project selector */}
        <div class="upload-modal-field">
          <label class="upload-modal-label">Target project</label>
          {projectNames.length > 0 ? (
            <select
              class="upload-modal-select"
              value={projectKey}
              onChange={(e) => setProjectKey((e.target as HTMLSelectElement).value)}
              disabled={stage === 'uploading'}
            >
              <option value="">— select project —</option>
              {projectNames.map((p) => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>
          ) : (
            <input
              class="upload-modal-input"
              type="text"
              placeholder="e.g. voice-assistant"
              value={projectKey}
              onInput={(e) => setProjectKey((e.target as HTMLInputElement).value)}
              disabled={stage === 'uploading'}
            />
          )}
        </div>

        {/* Drop zone */}
        {stage === 'idle' && (
          <div
            class={`upload-modal-dropzone${dragOver ? ' drag-over' : ''}`}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            onClick={() => inputRef.current?.click()}
          >
            <span class="upload-modal-dropzone-icon">📄</span>
            <span class="upload-modal-dropzone-text">
              Drop PDF here or <span class="upload-modal-link">browse</span>
            </span>
            <span class="upload-modal-dropzone-hint">PDF only · max 50 MB</span>
            <input
              ref={inputRef}
              type="file"
              accept=".pdf,application/pdf"
              style={{ display: 'none' }}
              onChange={handleFileSelect}
            />
          </div>
        )}

        {/* Progress */}
        {stage === 'uploading' && (
          <div class="upload-modal-progress">
            <div class="upload-modal-spinner" />
            <span class="upload-modal-progress-text">{progress}</span>
          </div>
        )}

        {/* Success */}
        {stage === 'done' && result && (
          <div class="upload-modal-success">
            <span class="upload-modal-success-icon">✓</span>
            <div class="upload-modal-success-info">
              <span class="upload-modal-success-title">{result.title}</span>
              <span class="upload-modal-success-meta">{result.wordCount.toLocaleString()} words</span>
            </div>
            <div class="upload-modal-success-actions">
              <button class="upload-modal-btn-primary" onClick={() => openFile(result.markdownPath)}>
                Open
              </button>
              <button class="upload-modal-btn-secondary" onClick={reset}>
                Upload another
              </button>
            </div>
          </div>
        )}

        {/* Error */}
        {stage === 'error' && (
          <div class="upload-modal-error">
            <span class="upload-modal-error-icon">⚠</span>
            <span class="upload-modal-error-msg">{errorMsg}</span>
            <button class="upload-modal-btn-secondary" onClick={reset}>Retry</button>
          </div>
        )}
      </div>
    </div>
  );
}
